import { useState, useEffect, useMemo, Fragment } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Checkbox } from '../../ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { Calendar as CalendarComponent } from '../../ui/calendar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../../ui/dropdown-menu';
import {
  ShoppingCart,
  Plus,
  Search,
  Filter,
  MoreVertical,
  Edit,
  Eye,
  Download,
  CheckCircle,
  Clock,
  X,
  XCircle,
  Trash2,
  Send,
  FileText,
  Calendar,
  Package,
  RefreshCw,
  Copy,
  Archive,
  MessageSquare
} from 'lucide-react';
import { PurchaseOrderModal } from './PurchaseOrderModal';
import { PurchaseOrderDetailView } from './PurchaseOrderDetailView';
import { PurchaseOrderApprovalModal } from './PurchaseOrderApprovalModal';
import { procurementService, PurchaseOrder } from '../../../src/services/procurement.service';
import { authService } from '../../../src/services/auth.service';
import { poPDFGenerator } from '../../../src/utils/po-pdf-generator';
import { showSuccess, showError, showDeleteConfirm, showConfirm } from '../../../src/utils/notification.utils';
import { notificationService } from '../../../src/services/notification.service';
import { usePermission } from '../../../src/utils/permissions';
import { useAuth } from '../../../src/hooks/useAuth';
import { useOrgCurrency } from '../../../src/hooks/useOrgCurrency';
import { formatCurrency as fmtCurShared, formatCurrencyCompact as fmtCurCompact } from '../../../src/utils/currency';
import { usePurchaseOrders } from '../../../src/hooks/queries/use-purchase-orders';
import { format } from 'date-fns';

interface FilterState {
  search: string;
  status: string;
  priority: string;
  vendor: string;
  project: string;
  dateFrom: Date | undefined;
  dateTo: Date | undefined;
  minAmount: string;
  maxAmount: string;
}

export function PurchaseOrders() {
  const { canWrite, canApprove, canDelete, canDo } = usePermission();
  const orgCurrency = useOrgCurrency();
  const formatCurrency = (amount: number) => fmtCurShared(amount, orgCurrency);
  const formatCompact = (amount: number) => fmtCurCompact(amount, orgCurrency);
  const canWritePO    = canWrite('/purchase-orders');
  const canApprovePO  = canApprove('/purchase-orders') || canDo('approve_po');
  const canDeletePO   = canDelete('/purchase-orders');
  const canSendVendor = canDo('send_po_to_vendor');

  const { orgId } = useAuth();
  const { data: purchaseOrders = [], isLoading: loading, error: poError, refetch: refetchPOs } = usePurchaseOrders(orgId);

  useEffect(() => {
    if (poError) {
      showError({ title: 'Load Failed', message: (poError as Error).message ?? 'Failed to load purchase orders' });
    }
  }, [poError]);
  const [selectedOrders, setSelectedOrders] = useState<Set<string>>(new Set());
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [viewingOrder, setViewingOrder] = useState<string | null>(null);
  const [editingOrder, setEditingOrder] = useState<string | null>(null);
  const [duplicatingOrder, setDuplicatingOrder] = useState<PurchaseOrder | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [approvingOrder, setApprovingOrder] = useState<{ order: PurchaseOrder; approvalId: string } | null>(null);

  const [filters, setFilters] = useState<FilterState>({
    search: '',
    status: 'all',
    priority: 'all',
    vendor: 'all',
    project: 'all',
    dateFrom: undefined,
    dateTo: undefined,
    minAmount: '',
    maxAmount: ''
  });


  // Save/load filters from localStorage
  useEffect(() => {
    const savedFilters = localStorage.getItem('po-filters');
    if (savedFilters) {
      try {
        const parsed = JSON.parse(savedFilters);
        setFilters(prev => ({ ...prev, ...parsed }));
      } catch (error) {
        console.error('Failed to parse saved filters:', error);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('po-filters', JSON.stringify(filters));
  }, [filters]);

  const fetchPurchaseOrders = () => refetchPOs();

  const filteredOrders = useMemo(() => {
    console.log('[PurchaseOrders] Filtering orders. Total:', purchaseOrders.length);
    console.log('[PurchaseOrders] Filters:', filters);

    const filtered = purchaseOrders.filter(order => {
      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesSearch =
          order.po_number?.toLowerCase().includes(searchLower) ||
          order.vendor_name?.toLowerCase().includes(searchLower) ||
          order.description?.toLowerCase().includes(searchLower);

        if (!matchesSearch) return false;
      }

      // Status filter
      if (filters.status !== 'all' && order.status !== filters.status) {
        return false;
      }

      // Date range filter
      if (filters.dateFrom) {
        const orderDate = new Date(order.po_date);
        if (orderDate < filters.dateFrom) return false;
      }

      if (filters.dateTo) {
        const orderDate = new Date(order.po_date);
        if (orderDate > filters.dateTo) return false;
      }

      // Amount range filter
      if (filters.minAmount) {
        const min = parseFloat(filters.minAmount);
        if (!isNaN(min) && Number(order.total_amount) < min) return false;
      }

      if (filters.maxAmount) {
        const max = parseFloat(filters.maxAmount);
        if (!isNaN(max) && Number(order.total_amount) > max) return false;
      }

      return true;
    });

    console.log('[PurchaseOrders] Filtered orders count:', filtered.length);
    return filtered;
  }, [purchaseOrders, filters]);

  const stats = useMemo(() => {
    const total = purchaseOrders.length;
    const pending = purchaseOrders.filter(po => po.status === 'pending_approval').length;
    const approved = purchaseOrders.filter(po => po.status === 'approved').length;
    const draft = purchaseOrders.filter(po => po.status === 'draft').length;
    const totalValue = purchaseOrders.reduce((sum, po) => sum + Number(po.total_amount || 0), 0);

    return { total, pending, approved, draft, totalValue };
  }, [purchaseOrders]);

  // Pagination
  const paginatedOrders = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredOrders.slice(startIndex, endIndex);
  }, [filteredOrders, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredOrders.length / pageSize);

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { className: string; label: string; icon: any }> = {
      draft: { className: 'bg-gray-100 text-gray-800', label: 'Draft', icon: FileText },
      pending_approval: { className: 'bg-yellow-100 text-yellow-800', label: 'Pending Approval', icon: Clock },
      approved: { className: 'bg-blue-100 text-blue-800', label: 'Approved', icon: CheckCircle },
      sent_to_vendor: { className: 'bg-purple-100 text-purple-800', label: 'Sent to Vendor', icon: Send },
      sent: { className: 'bg-purple-100 text-purple-800', label: 'Sent', icon: Send }, // Legacy support
      acknowledged: { className: 'bg-indigo-100 text-indigo-800', label: 'Acknowledged', icon: CheckCircle },
      partially_received: { className: 'bg-teal-100 text-teal-800', label: 'Partially Received', icon: Package },
      received: { className: 'bg-green-100 text-green-800', label: 'Received', icon: Package },
      completed: { className: 'bg-green-100 text-green-800', label: 'Completed', icon: CheckCircle },
      closed: { className: 'bg-slate-100 text-slate-800', label: 'Closed', icon: Archive },
      cancelled: { className: 'bg-red-100 text-red-800', label: 'Cancelled', icon: XCircle },
    };

    const config = statusConfig[status] || statusConfig.draft;
    const Icon = config.icon;

    return (
      <Badge className={config.className}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedOrders(new Set(filteredOrders.map(po => po.po_id)));
    } else {
      setSelectedOrders(new Set());
    }
  };

  const handleSelectOrder = (orderId: string, checked: boolean) => {
    const newSelected = new Set(selectedOrders);
    if (checked) {
      newSelected.add(orderId);
    } else {
      newSelected.delete(orderId);
    }
    setSelectedOrders(newSelected);
  };

  const handleBulkApprove = async () => {
    if (selectedOrders.size === 0) return;

    try {
      // Get organization ID
      const userProfile = await authService.getUserProfile();
      if (!userProfile?.organizationId) {
        throw new Error('Organization ID not found');
      }

      // Get pending approvals
      const approvalsResponse = await procurementService.getPendingApprovals(userProfile.organizationId);
      if (!approvalsResponse.success || !approvalsResponse.data) {
        throw new Error('Failed to fetch pending approvals');
      }

      const approvals = Array.isArray(approvalsResponse.data)
        ? approvalsResponse.data
        : ((approvalsResponse.data as any).data || []);

      // Filter approvals for selected orders
      const selectedOrdersArray = Array.from(selectedOrders);
      const approvalsToProcess = approvals.filter((a: any) =>
        selectedOrdersArray.includes(a.purchase_order_id || a.po_id)
      );

      if (approvalsToProcess.length === 0) {
        throw new Error('No pending approvals found for selected orders');
      }

      // Approve all
      const promises = approvalsToProcess.map((approval: any) =>
        procurementService.approvePurchaseOrder(approval.approval_id)
      );

      await Promise.all(promises);

      showSuccess({
        title: 'Bulk Approval Complete',
        message: `${approvalsToProcess.length} purchase orders approved`
      });

      setSelectedOrders(new Set());
      fetchPurchaseOrders();
    } catch (error: any) {
      showError({
        title: 'Bulk Approval Failed',
        message: error?.message || 'Some approvals may have failed'
      });
    }
  };

  const handleBulkSend = async () => {
    if (selectedOrders.size === 0) return;

    try {
      const promises = Array.from(selectedOrders).map(orderId =>
        procurementService.sendPurchaseOrderToVendor(orderId)
      );

      await Promise.all(promises);

      showSuccess({
        title: 'Bulk Send Complete',
        message: `${selectedOrders.size} purchase orders sent to vendors`
      });

      setSelectedOrders(new Set());
      fetchPurchaseOrders();
    } catch (error: any) {
      showError({
        title: 'Bulk Send Failed',
        message: error?.message || 'Some sends may have failed'
      });
    }
  };

  const handleBulkDownload = async () => {
    if (selectedOrders.size === 0) return;

    try {
      const ordersToDownload = purchaseOrders.filter(po =>
        selectedOrders.has(po.po_id)
      );

      await poPDFGenerator.generateBulkPOs(ordersToDownload);

      showSuccess({
        title: 'PDF Generated',
        message: `Downloaded PDF for ${selectedOrders.size} purchase orders`
      });
    } catch (error: any) {
      showError({
        title: 'Download Failed',
        message: error?.message || 'Failed to generate PDF'
      });
    }
  };

  const handleBulkDelete = async () => {
    if (selectedOrders.size === 0) return;

    const confirmed = await showDeleteConfirm(
      `${selectedOrders.size} purchase orders`,
      'purchase orders'
    );

    if (!confirmed) return;

    try {
      const promises = Array.from(selectedOrders).map(orderId =>
        procurementService.deletePurchaseOrder(orderId)
      );

      await Promise.all(promises);

      showSuccess({
        title: 'Bulk Delete Complete',
        message: `${selectedOrders.size} purchase orders deleted`
      });

      setSelectedOrders(new Set());
      fetchPurchaseOrders();
    } catch (error: any) {
      showError({
        title: 'Bulk Delete Failed',
        message: error?.message || 'Some deletions may have failed'
      });
    }
  };

  const handleDelete = async (orderId: string, poNumber: string) => {
    const confirmed = await showDeleteConfirm(poNumber, 'purchase order');

    if (!confirmed) return;

    try {
      await procurementService.deletePurchaseOrder(orderId);

      showSuccess({
        title: 'PO Deleted',
        message: `Purchase order ${poNumber} has been deleted`
      });

      fetchPurchaseOrders();
    } catch (error: any) {
      showError({
        title: 'Delete Failed',
        message: error?.message || 'Failed to delete purchase order'
      });
    }
  };

  const handleDownloadPDF = async (order: PurchaseOrder) => {
    try {
      // Fetch full order with items if not already loaded
      let orderWithItems = order;
      if (!order.items || order.items.length === 0) {
        const response = await procurementService.getPurchaseOrder(order.po_id);
        if (response.success && response.data) {
          const poData = (response.data as any).data || response.data;

          // Fetch items separately if still not included
          if (!poData.items || poData.items.length === 0) {
            const itemsResponse = await procurementService.getPurchaseOrderItems(order.po_id);
            if (itemsResponse.success && itemsResponse.data) {
              const items = (itemsResponse.data as any).data || itemsResponse.data;
              poData.items = Array.isArray(items) ? items : [];
            }
          }

          orderWithItems = poData;
        }
      }

      // Check if we have items now
      if (!orderWithItems.items || orderWithItems.items.length === 0) {
        showError({
          title: 'Download Failed',
          message: 'This purchase order has no line items'
        });
        return;
      }

      await poPDFGenerator.generatePO(orderWithItems);
      showSuccess({
        title: 'PDF Downloaded',
        message: `Purchase order ${order.po_number} PDF has been downloaded`
      });
    } catch (error: any) {
      console.error('[PurchaseOrders] PDF generation error:', error);
      showError({
        title: 'Download Failed',
        message: error?.message || 'Failed to generate PDF'
      });
    }
  };

  const handleSubmitForApproval = async (orderId: string, poNumber: string) => {
    try {
      await procurementService.submitForApproval(orderId);
      showSuccess({
        title: 'PO Submitted',
        message: `Purchase order ${poNumber} has been submitted for approval`
      });
      fetchPurchaseOrders();
    } catch (error: any) {
      showError({
        title: 'Submission Failed',
        message: error?.message || 'Failed to submit purchase order for approval'
      });
    }
  };

  const handleApprove = async (orderId: string, poNumber: string) => {
    try {
      // First get the organization ID
      const userProfile = await authService.getUserProfile();
      if (!userProfile?.organizationId) {
        throw new Error('Organization ID not found');
      }

      // Get pending approvals to find the approval_id for this PO
      const approvalsResponse = await procurementService.getPendingApprovals(userProfile.organizationId);
      if (!approvalsResponse.success || !approvalsResponse.data) {
        throw new Error('Failed to fetch pending approvals');
      }

      // Find the approval for this specific PO
      const approvals = Array.isArray(approvalsResponse.data)
        ? approvalsResponse.data
        : ((approvalsResponse.data as any).data || []);

      const approval = approvals.find((a: any) => a.purchase_order_id === orderId || a.po_id === orderId);
      if (!approval || !approval.approval_id) {
        throw new Error('Approval record not found for this purchase order');
      }

      // Find the order
      const order = purchaseOrders.find(po => po.po_id === orderId);
      if (!order) {
        throw new Error('Purchase order not found');
      }

      // Open the approval modal
      setApprovingOrder({ order, approvalId: approval.approval_id });
    } catch (error: any) {
      showError({
        title: 'Approval Setup Failed',
        message: error?.message || 'Failed to prepare approval'
      });
    }
  };

  const handleApprovalModalApprove = async (approvalId: string, comments: string) => {
    try {
      await procurementService.approvePurchaseOrder(approvalId, comments);
      showSuccess({
        title: 'PO Approved',
        message: `Purchase order has been approved`
      });
      const order = approvingOrder?.order;
      if (order?.created_by) {
        notificationService.poStatusChanged(order.created_by, order.po_number, 'Approved');
      }
      fetchPurchaseOrders();
    } catch (error: any) {
      showError({
        title: 'Approval Failed',
        message: error?.message || 'Failed to approve purchase order'
      });
      throw error;
    }
  };

  const handleApprovalModalReject = async (approvalId: string, rejectionReason: string, comments: string) => {
    try {
      await procurementService.rejectPurchaseOrder(approvalId, rejectionReason, comments);
      showSuccess({
        title: 'PO Rejected',
        message: `Purchase order has been rejected`
      });
      const order = approvingOrder?.order;
      if (order?.created_by) {
        notificationService.poStatusChanged(order.created_by, order.po_number, 'Rejected');
      }
      fetchPurchaseOrders();
    } catch (error: any) {
      showError({
        title: 'Rejection Failed',
        message: error?.message || 'Failed to reject purchase order'
      });
      throw error;
    }
  };

  const handleSendToVendor = async (orderId: string, poNumber: string) => {
    try {
      await procurementService.sendPurchaseOrderToVendor(orderId);
      showSuccess({
        title: 'PO Sent',
        message: `Purchase order ${poNumber} has been sent to vendor`
      });
      fetchPurchaseOrders();
    } catch (error: any) {
      showError({
        title: 'Send Failed',
        message: error?.message || 'Failed to send purchase order'
      });
    }
  };

  const handleAcknowledge = async (orderId: string, poNumber: string) => {
    try {
      await procurementService.acknowledgePurchaseOrder(orderId);
      showSuccess({
        title: 'PO Acknowledged',
        message: `Purchase order ${poNumber} has been marked as acknowledged`
      });
      fetchPurchaseOrders();
    } catch (error: any) {
      showError({
        title: 'Acknowledge Failed',
        message: error?.message || 'Failed to acknowledge purchase order'
      });
    }
  };

  const handleReject = async (orderId: string, poNumber: string) => {
    try {
      const userProfile = await authService.getUserProfile();
      if (!userProfile?.organizationId) {
        throw new Error('Organization ID not found');
      }

      const approvalsResponse = await procurementService.getPendingApprovals(userProfile.organizationId);
      if (!approvalsResponse.success || !approvalsResponse.data) {
        throw new Error('Failed to fetch pending approvals');
      }

      const approvals = Array.isArray(approvalsResponse.data)
        ? approvalsResponse.data
        : ((approvalsResponse.data as any).data || []);

      const approval = approvals.find((a: any) => a.purchase_order_id === orderId || a.po_id === orderId);
      if (!approval || !approval.approval_id) {
        throw new Error('Approval record not found for this purchase order');
      }

      // Find the order
      const order = purchaseOrders.find(po => po.po_id === orderId);
      if (!order) {
        throw new Error('Purchase order not found');
      }

      // Open the approval modal (it will handle rejection too)
      setApprovingOrder({ order, approvalId: approval.approval_id });
    } catch (error: any) {
      showError({
        title: 'Rejection Setup Failed',
        message: error?.message || 'Failed to prepare rejection'
      });
    }
  };

  const handleClose = async (orderId: string, poNumber: string) => {
    const confirmed = await showConfirm(
      'Close Purchase Order',
      `Are you sure you want to close purchase order "${poNumber}"? This action cannot be undone.`,
      'Close PO',
      'Cancel',
      'warning'
    );

    if (!confirmed) return;

    try {
      await procurementService.closePurchaseOrder(orderId, 'All items received and verified, closing PO');
      showSuccess({
        title: 'PO Closed',
        message: `Purchase order ${poNumber} has been closed`
      });
      fetchPurchaseOrders();
    } catch (error: any) {
      showError({
        title: 'Close Failed',
        message: error?.message || 'Failed to close purchase order'
      });
    }
  };

  const handleCancel = async (orderId: string, poNumber: string) => {
    const confirmed = await showConfirm(
      'Cancel Purchase Order',
      `Are you sure you want to cancel purchase order "${poNumber}"?`,
      'Cancel PO',
      'Keep',
      'destructive'
    );

    if (!confirmed) return;

    try {
      const comments = prompt('Please provide a reason for cancellation (optional):');
      await procurementService.cancelPurchaseOrder(orderId, comments || 'Cancelled by user');
      showSuccess({
        title: 'PO Cancelled',
        message: `Purchase order ${poNumber} has been cancelled`
      });
      fetchPurchaseOrders();
    } catch (error: any) {
      showError({
        title: 'Cancellation Failed',
        message: error?.message || 'Failed to cancel purchase order'
      });
    }
  };

  const handleDuplicate = (order: PurchaseOrder) => {
    setDuplicatingOrder(order);
  };

  const handleArchive = async (orderId: string, poNumber: string) => {
    const confirmed = await showConfirm(
      'Archive Purchase Order',
      `Are you sure you want to archive purchase order "${poNumber}"? The order will be cancelled and moved to archived status.`,
      'Archive',
      'Cancel',
      'warning'
    );

    if (!confirmed) return;

    try {
      await procurementService.cancelPurchaseOrder(orderId, 'Archived by user');
      showSuccess({
        title: 'PO Archived',
        message: `Purchase order ${poNumber} has been archived`
      });
      fetchPurchaseOrders();
    } catch (error: any) {
      showError({
        title: 'Archive Failed',
        message: error?.message || 'Failed to archive purchase order'
      });
    }
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: 'all',
      priority: 'all',
      vendor: 'all',
      project: 'all',
      dateFrom: undefined,
      dateTo: undefined,
      minAmount: '',
      maxAmount: ''
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-semibold">Purchase Orders</h2>
          <p className="text-muted-foreground">Manage purchase orders and procurement requests</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchPurchaseOrders} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {canWritePO && (
            <Button onClick={() => setShowCreateModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create PO
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <ShoppingCart className="h-4 w-4 text-blue-600" />
              Total Orders
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-700">{stats.total}</div>
            <p className="text-xs text-blue-600 mt-1">All purchase orders</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4 text-yellow-600" />
              Pending Approval
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-700">{stats.pending}</div>
            <p className="text-xs text-yellow-600 mt-1">Awaiting approval</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-blue-600" />
              Approved
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-700">{stats.approved}</div>
            <p className="text-xs text-blue-600 mt-1">Ready to send</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <FileText className="h-4 w-4 text-gray-600" />
              Draft
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-700">{stats.draft}</div>
            <p className="text-xs text-gray-600 mt-1">Incomplete orders</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <FileText className="h-4 w-4 text-green-600" />
              Total Value
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-700">{formatCompact(stats.totalValue)}</div>
            <p className="text-xs text-green-600 mt-1">All orders combined</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by PO number, vendor, or description..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="pl-10"
                />
              </div>

              <Select value={filters.status} onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="pending_approval">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>

              <Button
                variant={showFilters ? 'default' : 'outline'}
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="h-4 w-4 mr-2" />
                {showFilters ? 'Hide' : 'Show'} Filters
              </Button>

              {(filters.search || filters.status !== 'all' || filters.dateFrom || filters.dateTo) && (
                <Button variant="ghost" onClick={clearFilters}>
                  Clear
                </Button>
              )}
            </div>

            {/* Advanced Filters */}
            {showFilters && (
              <div className="grid grid-cols-4 gap-4 pt-4 border-t">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Date From</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start">
                        <Calendar className="h-4 w-4 mr-2" />
                        {filters.dateFrom ? format(filters.dateFrom, 'PP') : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <CalendarComponent
                        mode="single"
                        selected={filters.dateFrom}
                        onSelect={(date) => setFilters(prev => ({ ...prev, dateFrom: date }))}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Date To</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start">
                        <Calendar className="h-4 w-4 mr-2" />
                        {filters.dateTo ? format(filters.dateTo, 'PP') : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <CalendarComponent
                        mode="single"
                        selected={filters.dateTo}
                        onSelect={(date) => setFilters(prev => ({ ...prev, dateTo: date }))}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Min Amount</label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={filters.minAmount}
                    onChange={(e) => setFilters(prev => ({ ...prev, minAmount: e.target.value }))}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Max Amount</label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={filters.maxAmount}
                    onChange={(e) => setFilters(prev => ({ ...prev, maxAmount: e.target.value }))}
                  />
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions */}
      {selectedOrders.size > 0 && (
        <Card className="border-primary">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{selectedOrders.size} orders selected</span>
              <div className="flex gap-2">
                {canApprovePO && (
                  <Button variant="outline" size="sm" onClick={handleBulkApprove}>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Approve
                  </Button>
                )}
                {canSendVendor && (
                  <Button variant="outline" size="sm" onClick={handleBulkSend}>
                    <Send className="h-4 w-4 mr-2" />
                    Send
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={handleBulkDownload}>
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>
                {canDeletePO && (
                  <Button variant="destructive" size="sm" onClick={handleBulkDelete}>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Purchase Orders Table */}
      <Card>
        <CardHeader>
          <CardTitle>Purchase Orders</CardTitle>
          <CardDescription>
            Showing {filteredOrders.length} of {purchaseOrders.length} purchase orders
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No purchase orders found</p>
              <Button className="mt-4" onClick={() => setShowCreateModal(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create First PO
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={selectedOrders.size === filteredOrders.length}
                        onCheckedChange={handleSelectAll}
                      />
                    </TableHead>
                    <TableHead>PO Number</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Required Date</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedOrders.map(order => (
                    <TableRow key={order.po_id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedOrders.has(order.po_id)}
                          onCheckedChange={(checked) =>
                            handleSelectOrder(order.po_id, checked as boolean)
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{order.po_number}</p>
                          <p className="text-sm text-muted-foreground">{order.items_count || order.items?.length || 0} items</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="font-medium">{order.vendor_name}</p>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <p>{new Date(order.po_date).toLocaleDateString()}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {(order.requested_delivery_date || order.required_date) ? (
                            <p>{new Date(order.requested_delivery_date || order.required_date).toLocaleDateString()}</p>
                          ) : (
                            <p className="text-muted-foreground">-</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {(order as any).priority ? (
                          <Badge variant={(order as any).priority === 'urgent' ? 'destructive' : (order as any).priority === 'high' ? 'default' : 'secondary'}>
                            {(order as any).priority}
                          </Badge>
                        ) : (
                          <Badge variant="secondary">normal</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <p className="font-medium">{formatCompact(Number(order.total_amount))}</p>
                      </TableCell>
                      <TableCell>{getStatusBadge(order.status)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {/* Quick Access Icons */}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setViewingOrder(order.po_id)}
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>

                          {order.status === 'draft' && canWritePO && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setEditingOrder(order.po_id)}
                              title="Edit PO"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          )}

                          {order.status === 'pending_approval' && canApprovePO && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleApprove(order.po_id, order.po_number)}
                              title="Approve PO"
                              className="text-green-600 hover:text-green-700"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                          )}

                          {order.status === 'approved' && canSendVendor && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleSendToVendor(order.po_id, order.po_number)}
                              title="Send to Vendor"
                              className="text-blue-600 hover:text-blue-700"
                            >
                              <Send className="h-4 w-4" />
                            </Button>
                          )}

                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDownloadPDF(order)}
                            title="Download PDF"
                          >
                            <Download className="h-4 w-4" />
                          </Button>

                          {/* More Actions Dropdown */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" title="More actions">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                              <DropdownMenuItem onClick={() => setViewingOrder(order.po_id)}>
                                <Eye className="h-4 w-4 mr-2" />
                                View Details
                              </DropdownMenuItem>

                              {order.status === 'draft' && (
                                <DropdownMenuItem onClick={() => setEditingOrder(order.po_id)}>
                                  <Edit className="h-4 w-4 mr-2" />
                                  Edit PO
                                </DropdownMenuItem>
                              )}

                              <DropdownMenuSeparator />

                              <DropdownMenuItem onClick={() => handleDownloadPDF(order)}>
                                <Download className="h-4 w-4 mr-2" />
                                Download PDF
                              </DropdownMenuItem>

                              <DropdownMenuSeparator />

                              {/* Submit for Approval - for draft POs */}
                              {order.status === 'draft' && (
                                <DropdownMenuItem
                                  onClick={() => handleSubmitForApproval(order.po_id, order.po_number)}
                                  className="text-blue-600"
                                >
                                  <Send className="h-4 w-4 mr-2" />
                                  Submit for Approval
                                </DropdownMenuItem>
                              )}

                              {/* Approve/Reject - for pending_approval POs */}
                              {order.status === 'pending_approval' && (
                                <Fragment key={`pending-${order.po_id}`}>
                                  <DropdownMenuItem
                                    onClick={() => handleApprove(order.po_id, order.po_number)}
                                    className="text-green-600"
                                  >
                                    <CheckCircle className="h-4 w-4 mr-2" />
                                    Approve PO
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => handleReject(order.po_id, order.po_number)}
                                    className="text-red-600"
                                  >
                                    <XCircle className="h-4 w-4 mr-2" />
                                    Reject PO
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                </Fragment>
                              )}

                              {/* Send to Vendor - for approved POs */}
                              {order.status === 'approved' && (
                                <Fragment key={`send-${order.po_id}`}>
                                  <DropdownMenuItem
                                    onClick={() => handleSendToVendor(order.po_id, order.po_number)}
                                    className="text-blue-600"
                                  >
                                    <Send className="h-4 w-4 mr-2" />
                                    Send to Vendor
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                </Fragment>
                              )}

                              {/* Acknowledge - for sent_to_vendor POs */}
                              {order.status === 'sent' && (
                                <Fragment key={`ack-${order.po_id}`}>
                                  <DropdownMenuItem
                                    onClick={() => handleAcknowledge(order.po_id, order.po_number)}
                                    className="text-indigo-600"
                                  >
                                    <CheckCircle className="h-4 w-4 mr-2" />
                                    Mark as Acknowledged
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                </Fragment>
                              )}

                              {/* Close PO - for received POs */}
                              {order.status === 'partially_received' && (
                                <Fragment key={`close-${order.po_id}`}>
                                  <DropdownMenuItem
                                    onClick={() => handleClose(order.po_id, order.po_number)}
                                    className="text-green-600"
                                  >
                                    <Archive className="h-4 w-4 mr-2" />
                                    Close PO
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                </Fragment>
                              )}

                              <DropdownMenuItem onClick={() => handleDuplicate(order)}>
                                <Copy className="h-4 w-4 mr-2" />
                                Duplicate PO
                              </DropdownMenuItem>

                              <DropdownMenuSeparator />

                              {/* Cancel - only for draft or pending_approval */}
                              {(order.status === 'draft' || order.status === 'pending_approval') && (
                                <DropdownMenuItem
                                  onClick={() => handleCancel(order.po_id, order.po_number)}
                                  className="text-red-600"
                                >
                                  <XCircle className="h-4 w-4 mr-2" />
                                  Cancel PO
                                </DropdownMenuItem>
                              )}

                              {order.status === 'draft' && (
                                <DropdownMenuItem
                                  className="text-destructive"
                                  onClick={() => handleDelete(order.po_id, order.po_number)}
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete Order
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-4 border-t">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Rows per page:</span>
                    <select
                      className="border rounded px-2 py-1 text-sm"
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      Page {currentPage} of {totalPages} ({filteredOrders.length} total)
                    </span>
                    <div className="flex gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                      >
                        First
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                      >
                        Last
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      <PurchaseOrderModal
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        mode="create"
        onSuccess={() => {
          fetchPurchaseOrders();
        }}
      />

      {editingOrder && (
        <PurchaseOrderModal
          open={!!editingOrder}
          onOpenChange={(open) => !open && setEditingOrder(null)}
          mode="edit"
          orderId={editingOrder}
          onSuccess={() => {
            fetchPurchaseOrders();
          }}
        />
      )}

      {viewingOrder && (
        <PurchaseOrderDetailView
          open={!!viewingOrder}
          onOpenChange={(open) => !open && setViewingOrder(null)}
          orderId={viewingOrder}
          onEdit={() => {
            setEditingOrder(viewingOrder);
            setViewingOrder(null);
          }}
          onDuplicate={(order) => {
            setDuplicatingOrder(order);
            setViewingOrder(null);
          }}
          onRefresh={fetchPurchaseOrders}
        />
      )}

      {duplicatingOrder && (
        <PurchaseOrderModal
          open={!!duplicatingOrder}
          onOpenChange={(open) => !open && setDuplicatingOrder(null)}
          mode="duplicate"
          duplicateData={duplicatingOrder}
          onSuccess={() => {
            fetchPurchaseOrders();
          }}
        />
      )}

      {approvingOrder && (
        <PurchaseOrderApprovalModal
          open={!!approvingOrder}
          onOpenChange={(open) => !open && setApprovingOrder(null)}
          order={approvingOrder.order}
          approvalId={approvingOrder.approvalId}
          onApprove={handleApprovalModalApprove}
          onReject={handleApprovalModalReject}
        />
      )}
    </div>
  );
}
